<template>
  <app-page class="lt-page" fill lean>
    <!-- Kopf wie Verkäufe (Kundenwunsch 13.10.2026 „titel etc nach unserem design anpassen“): leichter Titel.
         „Aktualisieren“ steht ohne Filter und Gruppierung rechts in der Werkzeugleiste statt in der Titelzeile
         (Kundenwunsch 13.10.2026: „damit dort kein leerer platz ist“); Handy: Icon-Knopf im Kopf. Die Karte lädt
         zusätzlich alle 15 Sekunden neu -->
    <template v-if="handy" #actions>
      <app-icon-button icon="refresh" label="Aktualisieren" @click="refresh" />
    </template>
    <template v-if="!handy" #toolbar-end>
      <app-button icon="refresh" @click="refresh">Aktualisieren</app-button>
    </template>
    <!-- Werkzeugleiste mit Feldknöpfen wie Verkäufe statt Vuetify-Feldern: Ansicht, Linie bzw. Drucker, Zeitraum,
         Zeitpunkt und Haltestellen ein/aus -->
    <template #toolbar>
      <app-select
        :value="selectedView"
        :items="viewItems"
        prefix="Ansicht"
        :menu-width="200"
        @input="selectedView = $event"
      />
      <app-select
        v-if="selectedView === 'line'"
        class="lt-wide"
        :value="selectedLine ? selectedLine.line_id : null"
        :items="lineItems"
        prefix="Linie"
        placeholder="bitte wählen"
        :menu-width="320"
        @input="selectLine"
      />
      <app-select
        v-if="selectedView === 'device'"
        class="lt-wide"
        :value="selectedDevice ? selectedDevice.printer_id : null"
        :items="deviceItems"
        prefix="Drucker"
        placeholder="bitte wählen"
        :menu-width="280"
        @input="selectDevice"
      />
      <app-select
        :value="Number(period) || 0"
        :items="periodItems"
        prefix="Zeitraum"
        :menu-width="200"
        @input="period = $event"
      />
      <date-time-field :date="startDate" :time="startTime" @apply="onMomentApply" />
      <!-- Haltestellen auf der Karte: standardmäßig aus (Kundenwunsch 13.10.2026), Umschaltknopf mit gedrücktem Zustand -->
      <app-button icon="pin" :pressed="showStops" @click="showStops = !showStops">Haltestellen</app-button>
    </template>

    <!-- Karte und Fahrplan (300) als verschachtelte Karten wie im Dashboard, mit Abstand zum Seitenrand (Kundenwunsch
         13.10.2026: „die karte bitte in eine abgerundete karte mit border so wie auch im dashboard mit etwas abstand nach
         außen hin“) -->
    <div class="map-content">
      <app-panel nested class="map-panel" icon="pin" :title="mapTitle" flush>
        <!-- helle Insel wie die Live-Karte des Dashboards (app-hell): Karte und Namensnennung bleiben auch im
             Dunkelmodus hell -->
        <div class="map-box app-hell">
          <div id="map" ref="mapContainer" class="map-canvas"></div>
          <!-- Namensnennung (Pflicht der OpenStreetMap-Lizenz): klein als „©“, voller Text bei Hover oder Fokus -->
          <span class="map-attr">© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap-Mitwirkende</a></span>
        </div>
        <!-- Legende der Ampel auf dem Rahmen unter der Karte wie im Dashboard -->
        <template #foot>
          <span class="app-legend" aria-label="Verspätung">
            <span v-for="item in delayLegend" :key="item.key" class="app-legend__item"><span
              class="app-legend__dot"
              :style="{ '--dot': item.dot }"
            ></span>{{ item.text }}</span>
          </span>
        </template>
      </app-panel>

      <app-panel
        nested
        class="map-side"
        icon="calClock"
        title="Fahrplan"
        flush
        :state="timetables.length === 0 ? 'empty' : null"
        state-icon="bus"
        state-title="Keine Fahrt ausgewählt"
        state-text="Bus auf der Karte anklicken."
      >
        <ul v-if="timetables.length > 0" class="map-stops">
          <li v-for="(timetable, i) in timetables" :key="i">
            <button
              type="button"
              class="map-stop"
              data-flush
              @click="focusTimetable(timetable)"
            >
              <span class="map-stop__time">{{ timetable.start.substr(0,5) }}</span>
              <span class="map-stop__name">{{ timetable.name }}</span>
              <span
                v-if="!(timetable.gps_latitude_to && timetable.gps_longitude_to)"
                class="map-stop__hint"
              >ohne Position</span>
            </button>
          </li>
        </ul>
      </app-panel>
    </div>
  </app-page>
</template>



<script>
import L from "leaflet";
import { latLng } from "leaflet";
import { Icon } from "leaflet";
import "leaflet-routing-machine";
import axios from "axios";
import _ from "lodash";
import moment from "moment";
import DateTimeField from "@/components/analytics/DateTimeField";
import icons from "@/components/ui/icons";
import { DELAY_LEGEND, delayLevel } from "@/components/overview/overviewData";
import { busMarkHtml, escapeHtml } from "@/components/overview/busMark";

delete Icon.Default.prototype._getIconUrl;

Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png")
});

// Zeitraum der Spuren in Minuten (früher freie Eingabe): übliche Werte; ein anderer Wert bleibt wählbar
const PERIODS = [5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 240, 480, 720, 1440];

// Kartendarstellung wie die Live-Karte des Dashboards (Kundenwunsch 13.10.2026): Busmarke (busMark.js, Stile ov-bus in
// bedienelemente.css) mit Liniennummer, Richtung (H/R) und Ampelpunkt – pulsierend bei einer Position von höchstens
// 3 Minuten vor jetzt bzw. vor dem gewählten Zeitpunkt, sonst Blitz mit Schrägstrich –, Spurpunkte in den Ampelfarben,
// Haltestellen als blaue H-Schilder (standardmäßig ausgeblendet), Tooltips dunkel wie die App-Tooltips. Freitexte aus
// den Antworten (comment, username, Ortsnamen) werden maskiert.
const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

function hasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

// Tooltip als Tabelle aus Bezeichnung und Wert (leere Werte entfallen)
function tipHtml(title, rows) {
  const body = rows
    .filter(row => hasValue(row[1]))
    .map(row => `<span class="lt-tip__k">${escapeHtml(row[0])}</span><span class="lt-tip__v">${escapeHtml(row[1])}</span>`)
    .join("");
  return `<div class="lt-tip">${title ? `<div class="lt-tip__title">${escapeHtml(title)}</div>` : ""}<div class="lt-tip__grid">${body}</div></div>`;
}

// Verspätung in Minuten („4 Min.“, früh mit Minus)
function delayText(seconds) {
  if (!hasValue(seconds) || !isFinite(Number(seconds))) {
    return null;
  }
  return `${Math.round(Number(seconds) / 60)} Min.`;
}

// Haltestellen-Schild: Bus-Icon (Lucide bus) statt „H“ (Kundenwunsch 13.10.2026)
const STOP_ICON = `<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${icons.bus}</svg>`;

function svgIcon(name) {
  return `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${icons[name]}</svg>`;
}

function periodText(minutes) {
  if (minutes >= 60 && minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? "1 Stunde" : `${hours} Stunden`;
  }
  return `${minutes} Minuten`;
}

export default {
  components: { DateTimeField },
  computed: {
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    delayLegend() {
      return DELAY_LEGEND;
    },
    // „Live-Karte“ wie im Dashboard; mit gewähltem Zeitpunkt nur „Karte“
    mapTitle() {
      return this.startDateTime ? "Karte" : "Live-Karte";
    },
    viewItems() {
      return this.views.map(view => ({ value: view.id, text: view.description }));
    },
    lineItems() {
      return this.lines.map(line => ({ value: line.line_id, text: `${line.line_id} ${line.description}` }));
    },
    deviceItems() {
      return this.devices.map(device => ({
        value: device.printer_id,
        text: `${device.printer_id}${device.comment ? " " + device.comment : ""}`
      }));
    },
    periodItems() {
      const current = Number(this.period) || 0;
      const values = PERIODS.indexOf(current) === -1 && current > 0 ? PERIODS.concat(current).sort((a, b) => a - b) : PERIODS;
      return values.map(value => ({ value, text: periodText(value) }));
    },
    startDateTime() {
      if (!this.startTime || !this.startDate) return null;
      const dateTime =
        moment(
          moment(this.startDate).format("YYYY-MM-DD") + " " + this.startTime
        )
          .utc()
          .format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z";
      return dateTime;
    }
  },
  data() {
    return {
      startTime: null,
      // Haltestellen auf der Karte (standardmäßig aus; geladen erst beim ersten Einschalten)
      showStops: false,
      startDate: null,
      period: 20,
      locations: [],
      interval: null,
      gps: [],
      bounds: null,
      timetableBounds: null,
      lines: [],
      devices: [],
      timetables: [],
      selectedView: "overview",
      selectedLine: null,
      selectedDevice: null,
      pause: false,
      detailLine: null,
      views: [
        {
          id: "overview",
          description: "Übersicht"
        },
        {
          id: "line",
          description: "Linie"
        },
        {
          id: "device",
          description: "Drucker"
        }
      ],
      markersLayer: null,
      routingLayer: null,
      lineLayer: null,
      lineMarkersLayer: null,
      locationLayer: null,
      map: null
    };
  },
  watch: {
    startDateTime() {
      this.gps = [];
      this.readGps();
    },
    period() {
      this.gps = [];
      this.readGps();
    },
    showStops(show) {
      if (show && this.locations.length === 0) {
        this.readLocations();
      } else {
        this.renderLocations();
      }
    },
    gps(_, old) {
      this.renderMarkers(Array.isArray(old) && old.length == 0);
      this.renderRoute();
    },
    locations() {
      this.renderLocations();
    },
    selectedView(newView) {
      this.gps = [];
      this.pause = true;
      this.resetLineDetails();

      if (newView == "line") {
        this.readLines();
      } else if (newView == "device") {
        this.readDevices();
      } else {
        this.pause = false;
        this.readGps();
      }
    },
    selectedDevice() {
      this.gps = [];
      this.pause = false;
      this.readGps();
    },
    selectedLine() {
      this.gps = [];
      this.pause = false;
      this.readGps();
    },
    touch() {
      this.$nextTick(this.invalidateMapSize);
    }
  },
  methods: {
    refresh() {
      if (this.showStops) {
        this.readLocations();
      }
      this.readGps();
    },
    selectLine(lineId) {
      this.selectedLine = this.lines.find(line => line.line_id === lineId) || null;
    },
    selectDevice(printerId) {
      this.selectedDevice = this.devices.find(device => device.printer_id === printerId) || null;
    },
    // Zeitpunkt aus dem Feld (beide null: live)
    onMomentApply({ date, time }) {
      this.startDate = date;
      this.startTime = time;
    },
    // Karte nach jeder Größenänderung des Containers neu vermessen (Seitenleiste ein-/ausklappen, Dichte, Fenster)
    observeMapSize() {
      if (typeof window.ResizeObserver === "undefined" || !this.$refs.mapContainer) {
        return;
      }
      this.mapResizeObserver = new window.ResizeObserver(this.invalidateMapSize);
      this.mapResizeObserver.observe(this.$refs.mapContainer);
    },
    unobserveMapSize() {
      if (this.mapResizeObserver) {
        this.mapResizeObserver.disconnect();
        this.mapResizeObserver = null;
      }
    },
    invalidateMapSize() {
      if (this.map) {
        this.map.invalidateSize();
      }
    },
    resetLineDetails() {
      this.timetables = [];
      this.detailLine = null;
      this.renderLineRoute();
      this.renderLineMarkers();
      this.renderLocations();
      this.renderMarkers();
    },
    // Tooltip einer GPS-Zeile (Bus oder Spurpunkt)
    gpsTip(gps) {
      const printer = [gps.printer_id, gps.comment].filter(hasValue).join(" · ");
      const driver = [gps.username, hasValue(gps.user_id) ? `(${gps.user_id})` : null].filter(hasValue).join(" ");
      const direction = hasValue(gps.line_id) ? (gps.is_outward_direction ? "Hinfahrt" : "Rückfahrt") : null;
      return tipHtml(hasValue(gps.line_id) ? `Linie ${gps.line_id}` : "Ohne Linie", [
        ["Richtung", direction],
        ["Fahrt", gps.trip_id],
        ["Drucker", printer],
        ["Fahrer", driver],
        ["Geschw.", isFinite(Number(gps.speed)) && hasValue(gps.speed) ? `${Math.round(gps.speed)} km/h` : null],
        ["Zeit", gps.time ? moment(gps.time).format("DD.MM.YYYY, HH:mm:ss") : null],
        ["Verspätung", delayText(gps.last_delay)]
      ]);
    },
    initMap() {
      // Namensnennung und Legende stehen als eigene Elemente über der Karte (wie im Dashboard)
      const map = L.map("map", { attributionControl: false }).setView([48.9853399, 12.9804694], 8);
      L.tileLayer(TILE_URL, { maxZoom: 18 }).addTo(map);
      this.map = map;

      // Kartenknöpfe mit Lucide-Icons und Titel (vorher Material-Icons ohne Beschriftung)
      L.easyButton(svgIcon("scan"), this.fitBounds, "Alle Busse zeigen").addTo(map);
      L.easyButton(svgIcon("x"), this.resetLineDetails, "Fahrplan ausblenden").addTo(map);
    },
    async readLocations() {
      const response = await axios.get(
        `${this.$store.state.baseUrl}location?customer_id=${this.$store.state.customerId}`
      );
      this.locations = response.data;
    },
    async readGps() {
      if (!this.pause) {
        let params =
          this.selectedView === "device" && this.selectedDevice
            ? `&printer_id=${this.selectedDevice.printer_id}`
            : "";
        params =
          params +
          (this.selectedView === "line" && this.selectedLine
            ? `&line_id=${this.selectedLine.line_id}`
            : "");

        const response = await axios.get(
          `${this.$store.state.baseUrl}gps?customer_id=${
            this.$store.state.customerId
          }&period=${this.period ? this.period : 0}${params}${
            this.startDateTime ? "&start=" + this.startDateTime : ""
          }`
        );
        this.gps = response.data;
      }
    },
    async readLines() {
      const response = await axios.get(
        `${this.$store.state.baseUrl}line?customer_id=${this.$store.state.customerId}`
      );
      this.lines = response.data;
    },
    async readDevices() {
      const response = await axios.get(
        `${this.$store.state.baseUrl}device?customer_id=${this.$store.state.customerId}`
      );
      this.devices = response.data.sort(function(a, b) {
        if (a.printer_id > b.printer_id) {
          return 1;
        }
        if (b.printer_id > a.printer_id) {
          return -1;
        }
        return 0;
      });
    },
    async readTimetables(e) {
      this.timetables = [];
      let trip_id = e.target.options.trip_id;
      this.detailLine = e.target.options.line_id;
      const response = await axios.get(
        `${this.$store.state.baseUrl}timetable?customer_id=${this.$store.state.customerId}&trip_id=${trip_id}`
      );
      this.timetables = response.data;
      this.renderLocations();
      this.renderMarkers();
      this.renderLineRoute();
      this.renderLineMarkers();
    },
    renderMarkers(fitBounds) {
      if (!this.markersLayer) {
        this.markersLayer = L.layerGroup().addTo(this.map);
      } else {
        this.markersLayer.clearLayers();
      }

      let waypointGps = [];
      let busGps = [];
      let waypointMarkers = [];
      let busMarkers = [];

      if (this.selectedView === "line") {
        busGps = _.values(_.groupBy(this.gps, "printer_id")).map(group =>
          _.first(group)
        );
        waypointGps = _.values(_.groupBy(this.gps, "printer_id"))
          .map(group => _.slice(group, 1))
          .flat();
      } else if (this.selectedView === "device") {
        if (_.first(this.gps)) {
          busGps = [_.first(this.gps)];
        }
        waypointGps = _.slice(this.gps, 1);
      } else {
        busGps = this.gps;
      }

      // Bezug für „Position höchstens 3 Minuten alt“: der gewählte Zeitpunkt, sonst jetzt
      const now = this.startDateTime ? Date.parse(this.startDateTime) : Date.now();
      busMarkers = busGps.map(gps => {
        const level = delayLevel(gps);
        const dim = this.detailLine != null && this.detailLine !== gps.line_id;
        const direction = hasValue(gps.line_id) ? (gps.is_outward_direction ? "H" : "R") : "";
        const marker = L.marker([gps.latitude, gps.longitude], {
          trip_id: gps.trip_id,
          line_id: gps.line_id,
          icon: L.divIcon({
            className: "ov-bus lt-bus",
            html: busMarkHtml(gps, { direction, now, dim }),
            iconSize: [44, 44],
            iconAnchor: [22, 22]
          }),
          // Verspätete oben wie im Dashboard; abgeblendete Linien darunter
          zIndexOffset: dim ? 500 : level === "late" ? 1200 : level === "warn" ? 1100 : 1000
        }).on("click", this.readTimetables);
        marker.bindTooltip(this.gpsTip(gps), { direction: "top", offset: [0, -14] });
        return marker;
      });

      waypointMarkers = waypointGps.map(waypoint => {
        const marker = L.marker([waypoint.latitude, waypoint.longitude], {
          icon: L.divIcon({
            className: "lt-trail-icon",
            html: `<span class="lt-trail lt-trail--${delayLevel(waypoint)}${this.detailLine != null ? " lt-dim" : ""}"></span>`,
            iconSize: [14, 14],
            iconAnchor: [7, 7]
          }),
          zIndexOffset: this.detailLine != null ? 100 : 900
        });
        marker.bindTooltip(this.gpsTip(waypoint), { direction: "top", offset: [0, -6] });
        return marker;
      });

      const allMarkers = [...busMarkers, ...waypointMarkers];
      L.layerGroup(allMarkers).addTo(this.markersLayer);
      this.bounds = L.featureGroup(allMarkers).getBounds();
      if (fitBounds) {
        this.fitBounds();
      }
    },
    focusTimetable(timetable) {
      this.map.flyTo(
        [timetable.gps_latitude_to, timetable.gps_longitude_to],
        15
      );
    },
    fitBounds() {
      if (this.timetableBounds && this.timetableBounds.isValid()) {
        this.map.fitBounds(this.timetableBounds, {
          padding: [50, 50]
        });
      } else if (this.bounds && this.bounds.isValid()) {
        this.map.fitBounds(this.bounds, {
          padding: [50, 50]
        });
      }
    },
    renderLocations() {
      if (!this.locationLayer) {
        this.locationLayer = L.layerGroup();
        this.map.addLayer(this.locationLayer);
      } else {
        this.locationLayer.clearLayers();
      }
      if (!this.showStops) {
        return;
      }

      const markers = [];

      this.locations.forEach(location => {
        if (location.gps_latitude_to && location.gps_longitude_to) {
          const marker = L.marker(
            [location.gps_latitude_to, location.gps_longitude_to],
            {
              icon: L.divIcon({
                className: "lt-stop-icon",
                html: `<span class="lt-stop${this.detailLine != null ? " lt-dim" : ""}">${STOP_ICON}</span>`,
                iconSize: [22, 22],
                iconAnchor: [11, 11]
              }),
              zIndexOffset: 500
            }
          );
          markers.push(marker);
          marker.bindTooltip(tipHtml(location.name, [["Ort-Nr.", location.location_id]]), { direction: "top", offset: [0, -8] });
        }
      });

      L.layerGroup(markers).addTo(this.locationLayer);
    },
    renderRoute() {
      if (!this.routingLayer) {
        this.routingLayer = L.layerGroup().addTo(this.map);
      } else {
        this.routingLayer.clearLayers();
      }
      if (this.selectedView === "line") {
        const printerGroups = _.values(_.groupBy(this.gps, "printer_id"));
        const polylines = printerGroups.map(group =>
          L.polyline(
            group.map(gps => latLng(gps.latitude, gps.longitude)),
            {
              className: "map-route",
              opacity: this.detailLine != null ? 0.3 : 1.0
            }
          )
        );
        polylines.forEach(polyline => polyline.addTo(this.routingLayer));
      } else if (this.selectedView === "device") {
        const polyline = L.polyline(
          this.gps.map(gps => latLng(gps.latitude, gps.longitude)),
          {
            className: "map-route",
            opacity: this.detailLine != null ? 0.3 : 1.0
          }
        );

        polyline.addTo(this.routingLayer);
      }
    },
    renderLineRoute() {
      if (!this.lineLayer) {
        this.lineLayer = L.layerGroup().addTo(this.map);
      } else {
        this.lineLayer.clearLayers();
      }

      const polyline = L.polyline(
        this.timetables
          .filter(
            timetable => timetable.gps_latitude_to && timetable.gps_longitude_to
          )
          .map(timetable =>
            latLng(timetable.gps_latitude_to, timetable.gps_longitude_to)
          ),
        { className: "map-plan" }
      );

      polyline.addTo(this.lineLayer);
    },
    renderLineMarkers() {
      if (!this.lineMarkersLayer) {
        this.lineMarkersLayer = L.layerGroup().addTo(this.map);
      } else {
        this.lineMarkersLayer.clearLayers();
      }

      const markers = [];

      this.timetables
        .filter(
          timetable => timetable.gps_latitude_to && timetable.gps_longitude_to
        )
        .forEach(timetable => {
          const time = timetable.start ? timetable.start.substr(0, 5) : "";
          const marker = L.marker(
            [timetable.gps_latitude_to, timetable.gps_longitude_to],
            {
              icon: L.divIcon({
                className: "lt-stop-icon lt-stop-icon--time",
                html: `<span class="lt-stop lt-stop--time">${escapeHtml(time)}</span>`,
                iconSize: [48, 24],
                iconAnchor: [24, 12]
              }),
              zIndexOffset: 900
            }
          );
          marker.bindTooltip(tipHtml(timetable.name, [["Abfahrt", time], ["Ort-Nr.", timetable.location_id]]), { direction: "top", offset: [0, -10] });
          markers.push(marker);
        });

      L.layerGroup(markers).addTo(this.lineMarkersLayer);
      this.timetableBounds = L.featureGroup(markers).getBounds();
    }
  },
  mounted() {
    this.initMap();
    this.readGps();
    this.interval = setInterval(this.readGps, 15000);
    this.observeMapSize();
  },
  beforeDestroy() {
    clearInterval(this.interval);
  },
  destroyed() {
    this.unobserveMapSize();
  }
};
</script>

<style scoped>
@import "../../../node_modules/leaflet/dist/leaflet.css";

/* Linie bzw. Drucker: lange Namen kürzt der Feldknopf mit Ellipse */
.lt-wide {
  max-width: 320px;
}

/* Karte und Fahrplan füllen das Blatt (AppPage fill) als zwei verschachtelte Karten mit Abstand zum Seitenrand */
.map-content {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  gap: 16px;
  padding: 16px var(--page-x, 20px) var(--page-x, 20px);
}
.map-panel {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
}
.map-panel ::v-deep .app-panel__body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
/* Kartenfläche in der inneren Karte (Ecken wie diese), Namensnennung darüber */
.map-box {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-radius: inherit;
  background: var(--map-bg);
}
.map-canvas {
  position: absolute;
  inset: 0;
  background: var(--map-bg);
  font-family: var(--font);
}
#map {
  z-index: 1 !important;
}
/* Kartenknöpfe wie Icon-Knöpfe: weiß mit weicher Kante, Radius 6; auf hellen Kacheln etwas kräftigerer Schatten */
.map-canvas ::v-deep .leaflet-bar {
  border: 1px solid var(--edge);
  border-radius: var(--r-ctl);
  box-shadow: var(--sh-map);
}
.map-canvas ::v-deep .leaflet-bar a,
.map-canvas ::v-deep .leaflet-bar button {
  color: var(--ink-2);
  border-bottom-color: var(--edge-soft);
}
/* Touch: Kartenknöpfe (Zoom und easyButton) 44 × 44, auch wenn Leaflet selbst kein Touch erkennt (Bedienung „Touch“) */
html.dichte-touch .map-canvas ::v-deep .leaflet-bar a,
html.dichte-touch .map-canvas ::v-deep .leaflet-bar button {
  width: 44px;
  height: 44px;
  line-height: 44px;
}
.map-side {
  flex: 0 0 300px;
  min-height: 0;
}
.map-side ::v-deep .app-panel__body {
  min-height: 0;
  overflow-y: auto;
}
.map-stops {
  list-style: none;
  margin: 0;
  padding: 4px 0;
}
.map-stop {
  width: 100%;
  min-height: var(--h-menu);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  border: 0;
  background: transparent;
  font: inherit;
  font-size: var(--fs-menu);
  line-height: var(--lh-menu);
  color: var(--ink);
  text-align: left;
  cursor: pointer;
}
:where(html:not(.dichte-touch)) .map-stop:hover {
  background: var(--hov);
}
.map-stop:active {
  background: var(--press);
}
.map-stop__time {
  flex: none;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}
.map-stop__name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.map-stop__hint {
  flex: none;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
/* Schmale Fenster: Seitenliste unter der Karte */
@media (max-width: 899.98px) {
  .map-content {
    flex-direction: column;
  }
  .map-side {
    flex: 0 0 40%;
  }
}
/* Handy: AppPage fill wirkt nicht (natürliche Höhe) – Karte mit fester Höhe, Liste darunter */
@media (max-width: 599.98px) {
  .map-content {
    flex: none;
    gap: 12px;
  }
  .map-panel {
    flex: none;
  }
  .map-box {
    flex: none;
    height: 60vh;
    min-height: 280px;
  }
  .map-side {
    flex: none;
  }
}
/* Routenlinien in Navy (Daten) statt Orange */
::v-deep .map-route {
  stroke: var(--d-foc);
}
::v-deep .leaflet-shadow-pane > .leaflet-marker-shadow {
  display: none;
}
::v-deep .leaflet-routing-container {
  display: none !important;
}
/* Fahrplan der angeklickten Fahrt: gestrichelt in der Datenfarbe (vorher Violett #7b75af) */
::v-deep .map-plan {
  stroke: var(--d-foc);
  stroke-width: 3;
  stroke-dasharray: 6 6;
  opacity: 0.9;
}
/* Kartenknöpfe: Lucide-Icons mittig */
.map-canvas ::v-deep .easy-button-button .button-state {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

/* ---------------------------------------------------------------- Namensnennung wie im Dashboard */
/* Namensnennung klein als „©“-Chip; der volle Text klappt bei Hover oder Fokus auf (wie OvLiveMap) */
.map-attr {
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 2;
  max-width: 20px;
  height: 20px;
  box-sizing: border-box;
  padding: 0 0 0 5px;
  overflow: hidden;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 0 0 1px rgba(21, 26, 33, 0.08), 0 1px 2px rgba(21, 26, 33, 0.08);
  font-size: 11px;
  line-height: 20px;
  white-space: nowrap;
  color: var(--ink-3);
  transition: max-width 0.2s ease, padding 0.2s ease;
}
.map-attr:hover,
.map-attr:focus-within {
  max-width: 260px;
  padding: 0 8px 0 6px;
}
.map-attr a {
  color: inherit;
}
@media (prefers-reduced-motion: reduce) {
  .map-attr {
    transition: none;
  }
}

/* ---------------------------------------------------------------- Marken (L.divIcon in der Karte) */
/* Bus: Marke aus dem Dashboard (ov-bus, bedienelemente.css), hier anklickbar (Fahrplan) und mit Richtung H/R */
.map-canvas ::v-deep .leaflet-marker-icon.lt-bus {
  cursor: pointer;
}
:where(html:not(.dichte-touch)) .map-canvas ::v-deep .lt-bus:hover .ov-bus__mark {
  border-color: var(--edge-hover);
}
/* abgeblendet, solange der Fahrplan einer anderen Linie offen ist */
.map-canvas ::v-deep .lt-dim {
  opacity: 0.35;
}
/* Spurpunkte: Ampelfarbe mit weißem Rand */
.map-canvas ::v-deep .lt-trail {
  display: block;
  width: 10px;
  height: 10px;
  margin: 2px;
  box-sizing: border-box;
  border: 2px solid #fff;
  border-radius: 50%;
  background: var(--st-none);
  box-shadow: 0 0 0 1px rgba(21, 26, 33, 0.18);
}
.map-canvas ::v-deep .lt-trail--ok {
  background: var(--st-ok);
}
.map-canvas ::v-deep .lt-trail--warn {
  background: var(--st-warn);
}
.map-canvas ::v-deep .lt-trail--late {
  background: var(--st-late);
}
/* Haltestellen: kleines Schild 18 in der Hauptfarbe (in der Karte immer Blau #2b7bb9) mit weißem Bus-Icon, weißem Rand
   1,5 und feinem Schatten (Kundenwünsche 13.10.2026, zuletzt „kannst du die haltestellen unser main blau machen bitte“,
   „die blauen haltestellen dann bitte doch wieder mit border“, „statt einem h ein bus icon darauf“); standardmäßig
   ausgeblendet. Im Fahrplan der Fahrt als weißer Chip mit Abfahrtszeit */
.map-canvas ::v-deep .lt-stop {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin: 2px;
  box-sizing: border-box;
  border: 1.5px solid #fff;
  border-radius: 5px;
  background: var(--link);
  box-shadow: 0 0 0 1px rgba(21, 26, 33, 0.12), 0 1px 2px rgba(21, 26, 33, 0.18);
  color: #fff;
}
.map-canvas ::v-deep .lt-stop svg {
  display: block;
}
.map-canvas ::v-deep .lt-stop--time {
  width: auto;
  height: 22px;
  margin: 1px auto;
  padding: 0 6px;
  border-color: var(--d-foc);
  border-radius: 5px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(21, 26, 33, 0.16);
  color: var(--ink);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}
.map-canvas ::v-deep .lt-stop-icon--time {
  display: flex;
  justify-content: center;
}

/* ---------------------------------------------------------------- Tooltips dunkel wie die App-Tooltips */
.map-canvas ::v-deep .leaflet-tooltip {
  padding: 6px 8px;
  border: 0;
  border-radius: var(--r-ctl);
  background: var(--tip-bg);
  box-shadow: 0 2px 8px rgba(21, 26, 33, 0.24);
  color: var(--tip-fg);
  font-family: var(--font);
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
}
.map-canvas ::v-deep .leaflet-tooltip-top::before {
  border-top-color: var(--tip-bg);
}
.map-canvas ::v-deep .leaflet-tooltip-bottom::before {
  border-bottom-color: var(--tip-bg);
}
.map-canvas ::v-deep .leaflet-tooltip-left::before {
  border-left-color: var(--tip-bg);
}
.map-canvas ::v-deep .leaflet-tooltip-right::before {
  border-right-color: var(--tip-bg);
}
.map-canvas ::v-deep .lt-tip__title {
  margin-bottom: 4px;
  font-weight: 600;
}
.map-canvas ::v-deep .lt-tip__grid {
  display: grid;
  grid-template-columns: auto auto;
  gap: 2px 12px;
}
.map-canvas ::v-deep .lt-tip__k {
  opacity: 0.7;
}
.map-canvas ::v-deep .lt-tip__v {
  font-variant-numeric: tabular-nums;
}
</style>
